/**
 * Sprint AI-UX · Phase 4 — Diplomexa Intelligence design-system preview.
 *
 * Super-admin-only showcase of every primitive shipped in
 * `components/diplomexa-intelligence/DiplomexaIntelligenceOutput.jsx`
 * rendered against sample data.  Lets the brand team review the
 * premium output language without spending Diploks on a real AI call,
 * and gives the testing agent a stable surface to validate the
 * components.
 */
import { useState } from "react";
import { Sparkles, ScrollText, Crown } from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import {
  DiplomexaIntelligenceOutputRenderer,
  DiplomexaBrandHeader,
  DiplomexaScoreRing,
  DiplomexaInsightBadge,
  DiplomexaOutputCard,
  DiplomexaChecklist,
  DiplomexaRecommendationCard,
  DiplomexaStrengthWeaknessGrid,
  DiplomexaNextActionsTimeline,
  DiplomexaMarkdownRenderer,
  DiplomexaExportToolbar,
} from "../../components/diplomexa-intelligence/DiplomexaIntelligenceOutput";

/* Sample Position Paper review (plain markdown, parser-friendly) */
const SAMPLE_POSITION_PAPER_OUTPUT = {
  brand: "Diplomexa Intelligence",
  content: `**Overall Score: 78/100**

Your position paper for Brazil in UNGA Third Committee on climate financing shows a strong policy stance and clear country authenticity. The writing is professional, with a few opportunities to sharpen evidence and solution depth.

## Strongest Points
- Clear country stance grounded in Brazil's BASIC group history and recent climate ambitions.
- Strong opening framing the agenda's moral and economic stakes.
- Effective use of multilateral language ("equitable burden-sharing", "common but differentiated responsibilities").

## Improvement Priorities
- Cite at least two specific UN reports or treaties to ground claims (e.g. UNFCCC Article 4.7, IPCC AR6 WG3).
- Strengthen the solutions section — add a concrete financing-instrument proposal (green-bond bridge, climate-debt swap, etc.).
- Avoid emotive phrasing in the final paragraph; lean diplomatic, not advocacy.

## Next Action Checklist
- Add 2 concrete citations to support your evidence section.
- Rewrite the solutions paragraph to include one actionable financing mechanism.
- Tighten the closing to a 3-sentence neutral diplomatic ask.
- Re-run the Diplomexa Intelligence review after edits.`,
};

/* Sample Readiness Report (deeper structure + tier) */
const SAMPLE_READINESS_REPORT_OUTPUT = {
  brand: "Diplomexa Readiness Coach",
  content: `**Overall Readiness Score: 82/100** — Tier: **Podium-ready**

You are tracking strongly against the Diplomexa Readiness Tier benchmarks for a delegate at your level. Your evidence and structure scores are excellent; your speech delivery still has the biggest unlock.

## Strengths
- Position paper structure is consistently top quartile across 4 submissions.
- Country-stance authenticity is excellent on Brazil and India committees.
- Caucus tactics show clear strategic thinking.

## Improvement Areas
- Opening speech delivery scored 64 — work on confidence + tempo.
- Resolution-writing clauses are sometimes too long; tighten verbs.
- Limited evidence of cross-bloc negotiation in last 3 simulations.

## Recommended Modules
- MUN 102 · Speech Practice Studio (4 sessions)
- MUN 103 · Bloc Negotiation Fundamentals
- Diplomexa Live Classroom · Crisis Caucus seminar

## Next 7-Day Action Plan
- Day 1 — Record a 3-minute opening speech and run it through Speech Practice Studio.
- Day 3 — Complete the Bloc Negotiation Fundamentals module.
- Day 5 — Submit a draft resolution to the Resolution Sandbox.
- Day 7 — Attend the Crisis Caucus seminar on Diplomexa Live Classroom.`,
};

const SAMPLE_PLAIN_TEXT_FALLBACK = {
  brand: "Diplomexa Intelligence",
  content:
    "This is a plain-text output without any markdown headings or bullet structure. " +
    "It should still render beautifully through the markdown renderer — wrapped " +
    "in a premium navy-and-gold card, with the brand header on top and an export " +
    "toolbar on the bottom. No raw text block, ever.",
};

const SAMPLE_NEXT_ACTIONS = [
  { day: 1, text: "Record a 3-minute opening speech in Speech Practice Studio." },
  { day: 3, text: "Complete the Bloc Negotiation Fundamentals module." },
  { day: 5, text: "Submit a draft resolution to the Resolution Sandbox." },
  { day: 7, text: "Attend the Crisis Caucus seminar on Diplomexa Live Classroom." },
];

export default function DiplomexaIntelligencePreviewPage() {
  const [view, setView] = useState("position-paper");

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-10" data-testid="ai-ux-preview-page">
        {/* Header */}
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37] font-semibold mb-2">
            Super Admin · AI-UX Phase 4
          </p>
          <h1
            className="font-serif text-4xl text-[#0A192F] leading-tight"
            style={{ fontFamily: '"Cormorant Garamond", serif' }}
          >
            Diplomexa Intelligence <i className="text-[#D4AF37]">Preview</i>
          </h1>
          <p className="text-sm text-slate-600 mt-3 max-w-2xl">
            A live preview of the new premium output renderer.  Sample data
            only — no Diploks consumed, no real AI calls.  Use this to
            validate the brand surface before shipping new tools.
          </p>
        </div>

        {/* View switcher */}
        <div className="flex flex-wrap gap-2">
          {[
            ["position-paper", "Position Paper", ScrollText],
            ["readiness", "Readiness Report", Crown],
            ["plain-text", "Plain-text fallback", Sparkles],
            ["primitives", "All primitives", Sparkles],
          ].map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              data-testid={`ai-ux-preview-view-${key}`}
              onClick={() => setView(key)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-xs rounded-full transition-colors ${
                view === key
                  ? "bg-[#0A192F] text-[#D4AF37]"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-[#D4AF37]"
              }`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {/* Demo surfaces */}
        {view === "position-paper" ? (
          <DiplomexaIntelligenceOutputRenderer
            output={SAMPLE_POSITION_PAPER_OUTPUT}
            title="Position Paper"
            emphasis="review"
            subtitle="Senior-chair-grade feedback across structure, evidence and diplomacy."
            icon={ScrollText}
            diploksUsed={6}
            testid="ai-ux-preview-paper"
          />
        ) : null}

        {view === "readiness" ? (
          <DiplomexaIntelligenceOutputRenderer
            output={SAMPLE_READINESS_REPORT_OUTPUT}
            title="Personal Readiness"
            emphasis="report"
            subtitle="Your strongest skills, gaps, and a focused 7-day plan."
            kicker="Diplomexa Readiness Coach"
            icon={Crown}
            diploksUsed={10}
            testid="ai-ux-preview-readiness"
          />
        ) : null}

        {view === "plain-text" ? (
          <DiplomexaIntelligenceOutputRenderer
            output={SAMPLE_PLAIN_TEXT_FALLBACK}
            title="Plain-text"
            emphasis="fallback"
            subtitle="Even unstructured output renders inside the premium frame."
            testid="ai-ux-preview-plain-text"
          />
        ) : null}

        {view === "primitives" ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DiplomexaOutputCard testid="ai-ux-preview-primitive-score">
              <div className="p-5">
                <DiplomexaBrandHeader
                  title="Score ring"
                  emphasis="(132 px)"
                  subtitle="Animated count-up with tier chip"
                />
                <div className="mt-5 flex flex-wrap items-center gap-6">
                  <DiplomexaScoreRing score={78} />
                  <DiplomexaScoreRing score={92} label="Speech" />
                  <DiplomexaScoreRing score={48} label="Resolution" />
                </div>
              </div>
            </DiplomexaOutputCard>

            <DiplomexaOutputCard testid="ai-ux-preview-primitive-badges">
              <div className="p-5">
                <DiplomexaBrandHeader title="Insight badges" emphasis="• tone palette" />
                <div className="mt-5 flex flex-wrap gap-2">
                  <DiplomexaInsightBadge tone="strength">3 strengths</DiplomexaInsightBadge>
                  <DiplomexaInsightBadge tone="warn">2 priorities</DiplomexaInsightBadge>
                  <DiplomexaInsightBadge tone="info">Diplomatic tone +6</DiplomexaInsightBadge>
                  <DiplomexaInsightBadge tone="gold">Podium-ready</DiplomexaInsightBadge>
                </div>
              </div>
            </DiplomexaOutputCard>

            <DiplomexaOutputCard testid="ai-ux-preview-primitive-checklist">
              <div className="p-5">
                <DiplomexaBrandHeader title="Checklist" emphasis="(animated tick)" />
                <div className="mt-4">
                  <DiplomexaChecklist items={[
                    "Add 2 concrete citations to support your evidence section.",
                    "Rewrite the solutions paragraph to include one financing mechanism.",
                    "Tighten the closing to a 3-sentence neutral diplomatic ask.",
                    "Re-run the Diplomexa Intelligence review after edits.",
                  ]} />
                </div>
              </div>
            </DiplomexaOutputCard>

            <DiplomexaOutputCard testid="ai-ux-preview-primitive-recommendations">
              <div className="p-5">
                <DiplomexaBrandHeader title="Recommendations" emphasis="(rows)" />
                <div className="mt-4 space-y-3">
                  <DiplomexaRecommendationCard
                    index={1}
                    title="Cite at least two UN reports"
                    body="Anchor your evidence section to specific resolutions to lift it from anecdotal to authoritative."
                  />
                  <DiplomexaRecommendationCard
                    index={2}
                    title="Add one concrete financing instrument"
                    body="A green-bond bridge or climate-debt swap example would tighten the proposed-solutions section."
                  />
                </div>
              </div>
            </DiplomexaOutputCard>

            <DiplomexaOutputCard testid="ai-ux-preview-primitive-grid" className="lg:col-span-2">
              <div className="p-5">
                <DiplomexaBrandHeader title="Strengths × Improvements" emphasis="grid" />
                <div className="mt-4">
                  <DiplomexaStrengthWeaknessGrid
                    strengths={[
                      "Clear country stance grounded in Brazil's BASIC group history.",
                      "Strong opening framing the agenda's moral stakes.",
                      "Effective use of multilateral language.",
                    ]}
                    weaknesses={[
                      "Add 2 specific UN reports as citations.",
                      "Strengthen the solutions section with a financing instrument.",
                      "Avoid emotive phrasing in the closing paragraph.",
                    ]}
                  />
                </div>
              </div>
            </DiplomexaOutputCard>

            <DiplomexaOutputCard testid="ai-ux-preview-primitive-timeline" className="lg:col-span-2">
              <div className="p-5">
                <DiplomexaBrandHeader title="Next 7-Day" emphasis="action timeline" />
                <div className="mt-5">
                  <DiplomexaNextActionsTimeline actions={SAMPLE_NEXT_ACTIONS} />
                </div>
              </div>
            </DiplomexaOutputCard>

            <DiplomexaOutputCard testid="ai-ux-preview-primitive-markdown" className="lg:col-span-2">
              <div className="p-5">
                <DiplomexaBrandHeader title="Markdown" emphasis="renderer" />
                <div className="mt-4">
                  <DiplomexaMarkdownRenderer
                    source={SAMPLE_POSITION_PAPER_OUTPUT.content}
                  />
                </div>
                <DiplomexaExportToolbar
                  source={SAMPLE_POSITION_PAPER_OUTPUT.content}
                  filename="diplomexa-sample"
                  diploksUsed={6}
                />
              </div>
            </DiplomexaOutputCard>
          </div>
        ) : null}
      </div>
    </DashboardLayout>
  );
}
